// Contexto "accounts" — papéis, permissões e hierarquia (docs/PLATAFORMA.md §7.2). Lógica pura, sem base de dados:
// usada pelo backend (que decide) e pelo backoffice (que esconde o que não se pode usar).
//
// Os PAPÉIS são dados (tabela "roles", geridos no backoffice → Papéis); as PERMISSÕES são código, porque é o
// código que as verifica. Todas as regras recebem o conjunto de papéis atual (`Roles`, criado com createRoles).
//
// Hierarquia: cada papel tem uma posição (rank). "player" é sempre a base (sem permissões) e "admin" o topo (todas
// as permissões, sempre). Quem não é admin só mexe no que está ESTRITAMENTE abaixo de si.

import { filledInActiveLangs } from "@ecliptica/i18n/langs";

export const PERMISSIONS = [
  "backoffice.access",
  "users.read",
  "users.notes",
  "users.compensate",
  "users.sanction",
  /** Atribuir papéis a contas (só papéis abaixo do próprio; admin atribui qualquer um). */
  "users.roles",
  "users.reset",
  "compensations.approve",
  "support.read",
  "support.reply",
  "content.read",
  "content.edit",
  "content.publish",
  "audit.read",
  "settings.edit",
  /** Ligar/desligar partes do site público (backoffice → Site). */
  "site.edit",
  /** Criar/editar/apagar/reordenar papéis (backoffice → Papéis), só abaixo do próprio. */
  "roles.manage",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export function isPermission(x: unknown): x is Permission {
  return typeof x === "string" && (PERMISSIONS as readonly string[]).includes(x);
}

/** Papéis fixos: o topo e a base da hierarquia. */
export const ADMIN_ROLE = "admin";
export const PLAYER_ROLE = "player";
export const BUILT_IN_ROLES = [PLAYER_ROLE, ADMIN_ROLE] as const;

export type RoleLabels = { pt: string; en: string };
export type RoleDef = {
  key: string;
  labels: RoleLabels;
  /** Posição guardada (maior = acima). As regras usam a ordem, não o número. */
  rank: number;
  permissions: readonly Permission[];
  /** Limite de compensação por ação em Cobres; null = sem limite. */
  compensationLimit: number | null;
  builtIn: boolean;
};

/**
 * Os cinco papéis de origem — iguais ao que a migração 0005_roles semeia (há um teste no backend que compara).
 * Servem também de reserva quando a app corre sem base de dados.
 * O designer não vê dados de utilizadores (princípio do mínimo necessário).
 */
export const DEFAULT_ROLES: readonly RoleDef[] = [
  { key: "player", labels: { pt: "Jogador", en: "Player" }, rank: 0, permissions: [], compensationLimit: 0, builtIn: true },
  {
    key: "support",
    labels: { pt: "Suporte", en: "Support" },
    rank: 1,
    permissions: ["backoffice.access", "users.read", "users.notes", "users.reset", "users.compensate", "support.read", "support.reply", "content.read"],
    compensationLimit: 200,
    builtIn: false,
  },
  {
    key: "moderator",
    labels: { pt: "Moderação", en: "Moderator" },
    rank: 2,
    permissions: ["backoffice.access", "users.read", "users.notes", "users.reset", "users.compensate", "users.sanction", "support.read", "support.reply", "content.read", "audit.read"],
    compensationLimit: 1000,
    builtIn: false,
  },
  {
    key: "designer",
    labels: { pt: "Design", en: "Designer" },
    rank: 3,
    // O designer decide quando o conteúdo (BD, livros, Compêndio) está pronto para o público → também liga as partes do site.
    permissions: ["backoffice.access", "content.read", "content.edit", "content.publish", "site.edit"],
    compensationLimit: 0,
    builtIn: false,
  },
  { key: "admin", labels: { pt: "Administração", en: "Admin" }, rank: 4, permissions: [...PERMISSIONS], compensationLimit: null, builtIn: true },
];

/**
 * Permissões arrumadas: sem repetidas nem desconhecidas, na ordem de PERMISSIONS. Quem tem alguma permissão é
 * staff e por isso tem sempre "backoffice.access" (e o 2FA obrigatório que vem com ele).
 */
export function normalizePermissions(perms: readonly unknown[]): Permission[] {
  const set = new Set(perms.filter(isPermission));
  if (set.size) set.add("backoffice.access");
  return PERMISSIONS.filter((p) => set.has(p));
}

/* ------------------------------------------------------------------ conjunto de papéis */

export type Roles = {
  /** Da base (player) para o topo (admin). */
  readonly list: readonly RoleDef[];
  get(key: unknown): RoleDef | undefined;
};

/**
 * Constrói o conjunto de papéis a partir das linhas da BD (ou de DEFAULT_ROLES), impondo as regras fixas:
 * admin no topo com todas as permissões e sem limite; player na base sem permissões nem limite.
 */
export function createRoles(defs: readonly RoleDef[]): Roles {
  const byKey = new Map<string, RoleDef>();
  for (const d of defs) byKey.set(d.key, { ...d, permissions: normalizePermissions(d.permissions), builtIn: false });
  const fallback = (key: string) => DEFAULT_ROLES.find((r) => r.key === key)!;
  const player = byKey.get(PLAYER_ROLE) ?? fallback(PLAYER_ROLE);
  const admin = byKey.get(ADMIN_ROLE) ?? fallback(ADMIN_ROLE);
  byKey.delete(PLAYER_ROLE);
  byKey.delete(ADMIN_ROLE);
  const middle = [...byKey.values()].sort((a, b) => a.rank - b.rank || a.key.localeCompare(b.key));
  const list: RoleDef[] = [
    { ...player, permissions: [], compensationLimit: 0, builtIn: true },
    ...middle,
    { ...admin, permissions: [...PERMISSIONS], compensationLimit: null, builtIn: true },
  ];
  const index = new Map(list.map((r) => [r.key, r]));
  return { list, get: (key) => (typeof key === "string" ? index.get(key) : undefined) };
}

/** Normaliza o que vem da base de dados (null ou vazio = "player"). */
export function roleKeyOf(x: unknown): string {
  return typeof x === "string" && x ? x : PLAYER_ROLE;
}

/** O papel de uma conta; desconhecido = player. */
export function roleOf(roles: Roles, key: unknown): RoleDef {
  return roles.get(roleKeyOf(key)) ?? roles.list[0]!;
}

/** Posição efetiva na hierarquia (0 = player; o maior é admin). Desconhecido = player. */
export function rankOf(roles: Roles, key: unknown): number {
  const k = roleOf(roles, key).key;
  return roles.list.findIndex((r) => r.key === k);
}

export const isAdmin = (key: unknown) => key === ADMIN_ROLE;

export function can(roles: Roles, role: unknown, permission: Permission): boolean {
  return roleOf(roles, role).permissions.includes(permission);
}

export function permissionsOf(roles: Roles, role: unknown): Permission[] {
  return [...roleOf(roles, role).permissions];
}

/** Staff = entra no backoffice (e por isso tem 2FA obrigatório). */
export function isStaff(roles: Roles, role: unknown): boolean {
  return can(roles, role, "backoffice.access");
}

/** Limite de compensação do papel (Infinity = sem limite). */
export function compensationLimitOf(roles: Roles, role: unknown): number {
  const l = roleOf(roles, role).compensationLimit;
  return l === null ? Number.POSITIVE_INFINITY : l;
}

/* ------------------------------------------------------------------ compensações */

/**
 * Valor de uma compensação em Cobres, para comparar com o limite.
 * Saqueta = 100 Cobres (PACK_PRICE do jogo). Pó Estelar: 4 Pó = 1 Cobre (provisório, revisto no W7 com a economia no servidor).
 */
export const PACK_PRICE = 100;
export const DUST_PER_COIN = 4;
export type CompensationAmount = { coins: number; dust: number; packs: number };

export function compensationValue(a: CompensationAmount): number {
  return a.coins + a.packs * PACK_PRICE + Math.ceil(a.dust / DUST_PER_COIN);
}

/** Dentro do limite do papel (compensationLimitOf) → aprovada logo; acima → fica à espera de quem tenha "compensations.approve". */
export function compensationStatus(limit: number, a: CompensationAmount): "approved" | "pending_approval" {
  return compensationValue(a) <= limit ? "approved" : "pending_approval";
}

/* ------------------------------------------------------------------ atribuir papéis */

type Actor = { id: string; role: unknown };
type Target = { id: string; role: unknown };
export type Check<R extends string> = { ok: true } | { ok: false; reason: R };

export type TargetReason = "forbidden" | "self" | "target_rank" | "last_admin";
export type AssignReason = TargetReason | "unknown_role" | "role_rank";

/**
 * Se o papel desta conta pode ser mudado por quem age (para a ficha mostrar/desativar o botão com o motivo):
 *  - precisa de "users.roles";
 *  - ninguém muda o seu próprio papel (nem para baixo);
 *  - quem não é admin só mexe em contas com papel ESTRITAMENTE abaixo do seu;
 *  - o último admin não pode deixar de o ser (adminCount = número de contas admin).
 */
export function canChangeRoleOf(roles: Roles, actor: Actor, target: Target, adminCount: number): Check<TargetReason> {
  if (!can(roles, actor.role, "users.roles")) return { ok: false, reason: "forbidden" };
  if (actor.id === target.id) return { ok: false, reason: "self" };
  if (!isAdmin(roleOf(roles, actor.role).key) && rankOf(roles, target.role) >= rankOf(roles, actor.role)) return { ok: false, reason: "target_rank" };
  if (isAdmin(roleOf(roles, target.role).key) && adminCount <= 1) return { ok: false, reason: "last_admin" };
  return { ok: true };
}

/** Papéis que quem age pode dar: admin → todos (incluindo admin, para haver vários); os outros → só abaixo do seu. */
export function assignableRoles(roles: Roles, actor: { role: unknown }): RoleDef[] {
  if (!can(roles, actor.role, "users.roles")) return [];
  if (isAdmin(roleOf(roles, actor.role).key)) return [...roles.list];
  const mine = rankOf(roles, actor.role);
  return roles.list.filter((_, i) => i < mine);
}

/** Regras completas para dar `newRole` à conta `target` (o backend chama isto antes de gravar). */
export function canAssignRole(roles: Roles, actor: Actor, target: Target, newRole: string, adminCount: number): Check<AssignReason> {
  if (!can(roles, actor.role, "users.roles")) return { ok: false, reason: "forbidden" };
  if (actor.id === target.id) return { ok: false, reason: "self" };
  if (!roles.get(newRole)) return { ok: false, reason: "unknown_role" };
  const t = canChangeRoleOf(roles, actor, target, adminCount);
  // O último admin pode "continuar admin" (não muda nada); qualquer outro papel deixaria a plataforma sem admin.
  if (!t.ok && !(t.reason === "last_admin" && isAdmin(newRole))) return t;
  if (!assignableRoles(roles, actor).some((r) => r.key === newRole)) return { ok: false, reason: "role_rank" };
  return { ok: true };
}

/* ------------------------------------------------------------------ sanções */

export type SanctionReason = "forbidden" | "self" | "target_rank";

/**
 * Quem pode suspender/banir/terminar sessões de quem: precisa de "users.sanction"; ninguém a si próprio;
 * quem não é admin só sanciona contas com papel ESTRITAMENTE abaixo do seu (ex.: moderação sanciona jogadores e
 * suporte, não design nem admin); um admin sanciona qualquer outra conta, incluindo outro admin (há sempre pelo
 * menos um admin ativo: o que age).
 */
export function canSanction(roles: Roles, actor: Actor, target: Target): Check<SanctionReason> {
  if (!can(roles, actor.role, "users.sanction")) return { ok: false, reason: "forbidden" };
  if (actor.id === target.id) return { ok: false, reason: "self" };
  if (!isAdmin(roleOf(roles, actor.role).key) && rankOf(roles, target.role) >= rankOf(roles, actor.role)) return { ok: false, reason: "target_rank" };
  return { ok: true };
}

/* ------------------------------------------------------------------ gerir papéis */

export type RoleInput = { labels: RoleLabels; permissions: readonly Permission[]; compensationLimit: number | null };

export type RoleEditReason =
  | "forbidden"
  | "unknown_role"
  | "role_rank"
  | "built_in"
  | "invalid_key"
  | "key_taken"
  | "invalid_labels"
  | "permission_not_held"
  | "limit_exceeds_own"
  | "in_use"
  | "cannot_move";

/** Chave de um papel novo: minúsculas, algarismos e "_", 2–32 caracteres, a começar por letra. */
export const ROLE_KEY_RE = /^[a-z][a-z0-9_]{1,31}$/;

/** Pode mexer neste papel? Precisa de "roles.manage"; admin mexe em todos; os outros só nos ESTRITAMENTE abaixo do seu. */
export function canManageRole(roles: Roles, actor: { role: unknown }, key: string): Check<"forbidden" | "unknown_role" | "role_rank"> {
  if (!can(roles, actor.role, "roles.manage")) return { ok: false, reason: "forbidden" };
  if (!roles.get(key)) return { ok: false, reason: "unknown_role" };
  if (isAdmin(roleOf(roles, actor.role).key)) return { ok: true };
  if (rankOf(roles, key) >= rankOf(roles, actor.role)) return { ok: false, reason: "role_rank" };
  return { ok: true };
}

const limitValue = (l: number | null) => (l === null ? Number.POSITIVE_INFINITY : l);

/**
 * Não se dá o que não se tem: quem não é admin não acrescenta permissões que não tenha, nem põe um limite de
 * compensação acima do seu. Tirar permissões (mesmo as que não tem) é permitido.
 */
function checkGrant(roles: Roles, actor: { role: unknown }, before: { permissions: readonly Permission[]; compensationLimit: number | null } | null, after: RoleInput): Check<"permission_not_held" | "limit_exceeds_own"> {
  if (isAdmin(roleOf(roles, actor.role).key)) return { ok: true };
  const mine = new Set(permissionsOf(roles, actor.role));
  const had = new Set(before?.permissions ?? []);
  if (normalizePermissions(after.permissions).some((p) => !had.has(p) && !mine.has(p))) return { ok: false, reason: "permission_not_held" };
  const changed = !before || limitValue(before.compensationLimit) !== limitValue(after.compensationLimit);
  if (changed && limitValue(after.compensationLimit) > compensationLimitOf(roles, actor.role)) return { ok: false, reason: "limit_exceeds_own" };
  return { ok: true };
}

/** Nome obrigatório nas línguas ATIVAS; nas inativas é opcional (e o valor guardado mantém-se: mergeLangTexts). */
const validLabels = (l: RoleLabels) => filledInActiveLangs(l);

/** Criar um papel. Nasce logo acima de "player" (abaixo de todos os outros) e depois sobe-se com "mover". */
export function checkRoleCreate(roles: Roles, actor: { role: unknown }, key: string, input: RoleInput): Check<RoleEditReason> {
  if (!can(roles, actor.role, "roles.manage")) return { ok: false, reason: "forbidden" };
  if (!ROLE_KEY_RE.test(key)) return { ok: false, reason: "invalid_key" };
  if (roles.get(key)) return { ok: false, reason: "key_taken" };
  if (!validLabels(input.labels)) return { ok: false, reason: "invalid_labels" };
  return checkGrant(roles, actor, null, input);
}

/**
 * Editar nome, permissões e limite. Nos papéis fixos só o nome muda (admin tem sempre tudo e sem limite;
 * player nunca tem permissões): tentar mudar o resto dá "built_in".
 */
export function checkRoleUpdate(roles: Roles, actor: { role: unknown }, key: string, input: RoleInput): Check<RoleEditReason> {
  const m = canManageRole(roles, actor, key);
  if (!m.ok) return m;
  if (!validLabels(input.labels)) return { ok: false, reason: "invalid_labels" };
  const current = roles.get(key)!;
  if (current.builtIn) {
    const samePerms = normalizePermissions(input.permissions).join() === current.permissions.join();
    if (!samePerms || limitValue(input.compensationLimit) !== limitValue(current.compensationLimit)) return { ok: false, reason: "built_in" };
    return { ok: true };
  }
  return checkGrant(roles, actor, current, input);
}

/** Apagar um papel: nunca os fixos, nunca com contas a usá-lo (userCount = contas com este papel). */
export function checkRoleDelete(roles: Roles, actor: { role: unknown }, key: string, userCount: number): Check<RoleEditReason> {
  const m = canManageRole(roles, actor, key);
  if (!m.ok) return m;
  if (roles.get(key)!.builtIn) return { ok: false, reason: "built_in" };
  if (userCount > 0) return { ok: false, reason: "in_use" };
  return { ok: true };
}

/**
 * Mover um papel uma posição para cima/baixo (troca com o vizinho). Os fixos não se movem e ninguém passa
 * para cima de admin nem para baixo de player. Quem não é admin só troca papéis que estejam ambos abaixo do seu
 * (logo, nada chega à sua posição nem acima).
 */
export function checkRoleMove(roles: Roles, actor: { role: unknown }, key: string, direction: "up" | "down"): { ok: true; swapWith: string } | { ok: false; reason: RoleEditReason } {
  const m = canManageRole(roles, actor, key);
  if (!m.ok) return m;
  if (roles.get(key)!.builtIn) return { ok: false, reason: "built_in" };
  const i = rankOf(roles, key);
  const other = roles.list[direction === "up" ? i + 1 : i - 1];
  if (!other || other.builtIn) return { ok: false, reason: "cannot_move" };
  const n = canManageRole(roles, actor, other.key);
  if (!n.ok) return n;
  return { ok: true, swapWith: other.key };
}

/* ------------------------------------------------------------------ estado da conta */

/** Estado de uma conta, para listas e filtros. */
export type AccountStatus = "active" | "suspended" | "banned" | "unverified";

export function accountStatus(u: { banned?: boolean | null; banExpires?: Date | string | null; emailVerified?: boolean | null }, now = new Date()): AccountStatus {
  if (u.banned) {
    if (!u.banExpires) return "banned";
    if (new Date(u.banExpires) > now) return "suspended";
  }
  return u.emailVerified ? "active" : "unverified";
}
